import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { themes, ThemeMode, THEME_OPTIONS } from '../theme/themes';
import { useAuth } from './AuthContext';

interface ThemeModeContextType {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  options: typeof THEME_OPTIONS;
}

const ThemeModeContext = createContext<ThemeModeContextType | undefined>(undefined);

const STORAGE_KEY = 'dmo_theme_mode';

const isThemeMode = (value: string | null | undefined): value is ThemeMode =>
  value === 'dark' || value === 'navy' || value === 'light';

export const ThemeModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const auth = useAuth();
  const [mode, setModeState] = useState<ThemeMode>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isThemeMode(stored) ? stored : 'light';
  });

  // Cached alongside the theme so a theme-only save doesn't clobber them —
  // PUT /api/users/me is full-replace, so every save must include the
  // user's current defaults, not just the field being changed.
  const profileDefaults = useRef<{ defaultBusinessId: string | null; defaultDeviceId: string | null } | null>(null);

  useEffect(() => {
    if (!auth.isSignedIn) {
      profileDefaults.current = null;
      return;
    }

    let cancelled = false;
    fetch('/api/users/me', {
      headers: { Authorization: `Bearer ${auth.tokens?.accessToken}` },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        profileDefaults.current = {
          defaultBusinessId: data.defaultBusinessId ?? null,
          defaultDeviceId: data.defaultDeviceId ?? null,
        };
        // Server is the source of truth once loaded — a saved preference
        // follows the user across devices/browsers instead of each one
        // falling back to its own localStorage default.
        if (isThemeMode(data.themePreference)) {
          localStorage.setItem(STORAGE_KEY, data.themePreference);
          setModeState(data.themePreference);
        }
      })
      .catch(() => {
        // Best-effort — fall back to whatever's in localStorage already.
      });

    return () => {
      cancelled = true;
    };
  }, [auth.isSignedIn, auth.tokens?.accessToken]);

  const setMode = useCallback((next: ThemeMode) => {
    localStorage.setItem(STORAGE_KEY, next);
    setModeState(next);

    if (!auth.isSignedIn || !profileDefaults.current) {
      return;
    }

    fetch('/api/users/me', {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${auth.tokens?.accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        defaultBusinessId: profileDefaults.current.defaultBusinessId,
        defaultDeviceId: profileDefaults.current.defaultDeviceId,
        themePreference: next,
      }),
    }).catch(() => {
      // Best-effort — theme still applies locally even if the save fails.
    });
  }, [auth.isSignedIn, auth.tokens?.accessToken]);

  const value = useMemo(() => ({ mode, setMode, options: THEME_OPTIONS }), [mode, setMode]);

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={themes[mode]}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};

export const useThemeMode = (): ThemeModeContextType => {
  const context = useContext(ThemeModeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeModeProvider');
  }
  return context;
};
